import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Heart, 
  MessageSquare, 
  Send, 
  MoreVertical, 
  Clock,
  Video,
  Image as ImageIcon,
  Trash,
  Edit,
  ChevronUp,
  ChevronDown,
  Loader
} from 'lucide-react';
import { useAuth } from '../../../core/contexts/AuthContext';
import useSocialFeed from '../hooks/useSocialFeed';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDocs,
  query,
  where,
  doc, 
  arrayUnion, 
  arrayRemove, 
  serverTimestamp, 
  orderBy,
  increment 
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';

const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { fetchPostById, formatTimestamp } = useSocialFeed();
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});

  // Fetch post and comments
  useEffect(() => {
    const fetchPostAndComments = async () => {
      setLoading(true);
      try {
        // Fetch the post
        const postData = await fetchPostById(postId);
        setPost(postData);
        
        // Fetch comments
        const commentsRef = collection(db, `posts/${postId}/comments`);
        const q = query(commentsRef, orderBy('createdAt', 'asc'));
        const snapshot = await getDocs(q);
        
        const fetchedComments = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Organize comments and replies
        const mainComments = fetchedComments.filter(comment => !comment.parentCommentId);
        const commentMap = {};
        
        mainComments.forEach(comment => {
          commentMap[comment.id] = {
            ...comment,
            replies: []
          };
        });
        
        fetchedComments
          .filter(comment => comment.parentCommentId)
          .forEach(reply => {
            if (commentMap[reply.parentCommentId]) {
              commentMap[reply.parentCommentId].replies.push(reply);
            }
          });
        
        setComments(Object.values(commentMap));
      } catch (err) {
        console.error('Error fetching post details:', err);
        setError('Could not load the post. It may have been deleted or you may not have permission to view it.');
      } finally {
        setLoading(false);
      }
    };

    if (postId) {
      fetchPostAndComments();
    }
  }, [postId, fetchPostById]);

  // Handle like/unlike a post
  const handleLikePost = async () => {
    if (!currentUser || !post) return;
    
    const postRef = doc(db, 'posts', postId);
    const userId = currentUser.uid;
    const isLiked = post.likes && post.likes.includes(userId);
    
    try {
      await updateDoc(postRef, {
        likes: isLiked ? arrayRemove(userId) : arrayUnion(userId),
        likesCount: isLiked ? increment(-1) : increment(1)
      });
      
      // Update local state
      setPost(prev => ({
        ...prev,
        likes: isLiked 
          ? prev.likes.filter(id => id !== userId) 
          : [...(prev.likes || []), userId],
        likesCount: isLiked ? (prev.likesCount - 1) : (prev.likesCount + 1)
      }));
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  // Submit a new comment
  const handleSubmitComment = async () => {
    if (!commentText.trim() || !currentUser || submittingComment) return;
    
    setSubmittingComment(true);
    
    try {
      const commentsRef = collection(db, `posts/${postId}/comments`);
      
      const commentData = {
        author: {
          avatar: userProfile?.photos?.[0] || '',
          name: userProfile?.basicInfo?.name || 'Anonymous',
          username: userProfile?.username || 'user',
        },
        authorId: currentUser.uid,
        content: commentText.trim(),
        createdAt: serverTimestamp(),
        likes: [],
        likesCount: 0,
        parentCommentId: null,
        postId: postId,
        replies: []
      };
      
      const docRef = await addDoc(commentsRef, commentData);
      
      // Update post's comment count
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        commentsCount: increment(1)
      });
      
      // Update local state
      setPost(prev => ({
        ...prev,
        commentsCount: (prev.commentsCount || 0) + 1
      }));
      
      // Add new comment to the list with the generated ID
      setComments(prev => [
        ...prev, 
        {
          ...commentData,
          id: docRef.id,
          replies: []
        }
      ]);
      
      setCommentText('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  // Submit a reply to a comment
  const handleSubmitReply = async (commentId) => {
    if (!replyText.trim() || !currentUser || submittingComment) return;
    
    setSubmittingComment(true);
    
    try {
      const commentsRef = collection(db, `posts/${postId}/comments`);
      
      const replyData = {
        author: {
          avatar: userProfile?.photos?.[0] || '',
          name: userProfile?.basicInfo?.name || 'Anonymous',
          username: userProfile?.username || 'user',
        },
        authorId: currentUser.uid,
        content: replyText.trim(),
        createdAt: serverTimestamp(),
        likes: [],
        likesCount: 0,
        parentCommentId: commentId,
        postId: postId,
      };
      
      const docRef = await addDoc(commentsRef, replyData);
      
      // Update parent comment's reply count
      const commentRef = doc(db, `posts/${postId}/comments`, commentId);
      await updateDoc(commentRef, {
        repliesCount: increment(1)
      });
      
      // Update local state
      setComments(prev => prev.map(comment => 
        comment.id === commentId
          ? {
              ...comment,
              repliesCount: (comment.repliesCount || 0) + 1,
              replies: [...(comment.replies || []), {
                ...replyData,
                id: docRef.id
              }]
            }
          : comment
      ));
      
      setReplyText('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Error adding reply:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  // Like/unlike a comment
  const handleLikeComment = async (commentId) => {
    if (!currentUser) return;
    
    const commentRef = doc(db, `posts/${postId}/comments`, commentId);
    const userId = currentUser.uid;
    
    // Find the comment in our local state
    let foundComment = null;
    let isMainComment = false;
    
    for (const comment of comments) {
      if (comment.id === commentId) {
        foundComment = comment;
        isMainComment = true;
        break;
      }
      
      for (const reply of comment.replies || []) {
        if (reply.id === commentId) {
          foundComment = reply;
          break;
        }
      }
      
      if (foundComment && !isMainComment) break;
    }
    
    if (!foundComment) return;
    
    const isLiked = foundComment.likes && foundComment.likes.includes(userId);
    
    try {
      await updateDoc(commentRef, {
        likes: isLiked ? arrayRemove(userId) : arrayUnion(userId),
        likesCount: isLiked ? increment(-1) : increment(1)
      });
      
      // Update local state
      if (isMainComment) {
        setComments(prev => prev.map(comment => 
          comment.id === commentId
            ? {
                ...comment,
                likes: isLiked 
                  ? comment.likes.filter(id => id !== userId) 
                  : [...(comment.likes || []), userId],
                likesCount: isLiked ? (comment.likesCount - 1) : (comment.likesCount + 1)
              }
            : comment
        ));
      } else {
        setComments(prev => prev.map(comment => {
          if (!comment.replies) return comment;
          
          const updatedReplies = comment.replies.map(reply => 
            reply.id === commentId
              ? {
                  ...reply,
                  likes: isLiked 
                    ? reply.likes.filter(id => id !== userId) 
                    : [...(reply.likes || []), userId],
                  likesCount: isLiked ? (reply.likesCount - 1) : (reply.likesCount + 1)
                }
              : reply
          );
          
          return {
            ...comment,
            replies: updatedReplies
          };
        }));
      }
    } catch (error) {
      console.error('Error liking/unliking comment:', error);
    }
  };

  // Delete a comment
  const handleDeleteComment = async (commentId, isReply = false, parentId = null) => {
    if (!currentUser) return;
    
    try {
      // Delete the comment document
      await deleteDoc(doc(db, `posts/${postId}/comments`, commentId));
      
      // If it's a main comment, also decrement post comment count
      if (!isReply) {
        const postRef = doc(db, 'posts', postId);
        await updateDoc(postRef, {
          commentsCount: increment(-1)
        });
        
        setPost(prev => ({
          ...prev,
          commentsCount: Math.max(0, (prev.commentsCount || 1) - 1)
        }));
        
        // Remove from local state
        setComments(prev => prev.filter(comment => comment.id !== commentId));
      } else if (parentId) {
        // Update parent comment's reply count
        const commentRef = doc(db, `posts/${postId}/comments`, parentId);
        await updateDoc(commentRef, {
          repliesCount: increment(-1)
        });
        
        // Remove reply from local state
        setComments(prev => prev.map(comment => 
          comment.id === parentId
            ? {
                ...comment,
                repliesCount: Math.max(0, (comment.repliesCount || 1) - 1),
                replies: comment.replies.filter(reply => reply.id !== commentId)
              }
            : comment
        ));
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  // Toggle reply form for a comment
  const toggleReplyForm = (commentId) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
    setReplyText('');
  };

  // Toggle expanded state for comment replies
  const toggleExpandReplies = (commentId) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  // Determine media type icon
  const getMediaTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return <Video size={16} className="mr-1" />;
      case 'image':
        return <ImageIcon size={16} className="mr-1" />;
      default:
        return null;
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-2xl py-10 px-4 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-green-500 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading post...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container mx-auto max-w-2xl py-10 px-4">
        <div className="bg-red-50 text-red-600 p-6 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Error</h2>
          <p>{error || 'Post not found.'}</p>
          <button 
            className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg flex items-center"
            onClick={handleBack}
          >
            <ArrowLeft size={18} className="mr-1" /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl pb-20 pt-4 px-4">
      <div className="flex items-center mb-4">
        <button 
          className="text-gray-600 hover:text-gray-800"
          onClick={handleBack}
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-bold text-center flex-1">Post Details</h1>
        <div className="w-6"></div> {/* Empty div for balance */}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        {/* Post Header */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
              <img 
                src={post.author?.avatar || 'https://via.placeholder.com/40'} 
                alt={post.author?.name || 'Anonymous'} 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="font-medium text-gray-900">{post.author?.name || 'Anonymous'}</div>
              <div className="text-xs text-gray-500 flex items-center">
                <span className="flex items-center">
                  <Clock size={12} className="mr-1" />
                  {post.createdAt ? formatTimestamp(post.createdAt) : 'Recently'}
                </span>
                {post.author?.username && (
                  <>
                    <span className="mx-1">•</span>
                    <span>@{post.author.username}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button className="text-gray-400 hover:text-gray-600">
            <MoreVertical size={18} />
          </button>
        </div>

        {/* Post Content */}
        <div className="px-4 py-2">
          <p className="text-gray-800 whitespace-pre-line">{post.content}</p>
        </div>

        {/* Post Media */}
        {post.media && (
          <div className="mt-2 relative">
            {post.media.type === 'image' ? (
              <img 
                src={post.media.url} 
                alt="Post attachment" 
                className="w-full h-auto"
              />
            ) : post.media.type === 'video' ? (
              <video 
                src={post.media.url} 
                controls 
                className="w-full h-auto"
                preload="metadata"
              />
            ) : null}
            <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full flex items-center">
              {getMediaTypeIcon(post.media.type)}
              {post.media.type.charAt(0).toUpperCase() + post.media.type.slice(1)}
            </div>
          </div>
        )}

        {/* Post Footer */}
        <div className="px-4 py-3 border-t border-gray-100 flex justify-between">
          <button 
            className={`flex items-center ${
              post.likes && currentUser && post.likes.includes(currentUser.uid) 
                ? 'text-red-500' 
                : 'text-gray-500 hover:text-red-500'
            }`}
            onClick={handleLikePost}
          >
            <Heart 
              size={18} 
              className="mr-1" 
              fill={post.likes && currentUser && post.likes.includes(currentUser.uid) ? "currentColor" : "none"} 
            />
            <span className="text-xs">{post.likesCount || 0}</span>
          </button>
          <div className="flex items-center text-gray-500">
            <MessageSquare size={18} className="mr-1" />
            <span className="text-xs">{post.commentsCount || 0}</span>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-medium text-gray-800">Comments ({post.commentsCount || 0})</h2>
        </div>

        {/* Comment Input */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-full overflow-hidden">
              <img 
                src={userProfile?.photos?.[0] || 'https://via.placeholder.com/32'} 
                alt="Your avatar" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 relative">
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none min-h-[60px]"
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                disabled={submittingComment}
              />
              <button 
                className="absolute right-2 bottom-2 text-green-500 disabled:text-gray-300 p-1"
                disabled={!commentText.trim() || submittingComment}
                onClick={handleSubmitComment}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Comments List */}
        {comments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No comments yet. Be the first to share your thoughts!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {comments.map(comment => (
              <div key={comment.id} className="p-4">
                {/* Main Comment */}
                <div className="flex space-x-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src={comment.author?.avatar || 'https://via.placeholder.com/32'} 
                      alt={comment.author?.name || 'User'} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div className="font-medium text-sm text-gray-900">
                          {comment.author?.name || 'User'}
                          {comment.author?.username && (
                            <span className="font-normal text-gray-500 text-xs ml-1">
                              @{comment.author.username}
                            </span>
                          )}
                        </div>
                        {currentUser && comment.authorId === currentUser.uid && (
                          <button 
                            className="text-gray-400 hover:text-red-500"
                            onClick={() => handleDeleteComment(comment.id)}
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                      <p className="text-gray-800 text-sm mt-1">{comment.content}</p>
                    </div>
                    
                    <div className="mt-1 text-xs flex items-center px-1">
                      <span className="text-gray-500">
                        {comment.createdAt ? formatTimestamp(comment.createdAt) : 'Just now'}
                      </span>
                      <button 
                        className={`ml-4 flex items-center ${
                          comment.likes && currentUser && comment.likes.includes(currentUser.uid)
                            ? 'text-red-500'
                            : 'text-gray-500 hover:text-red-500'
                        }`}
                        onClick={() => handleLikeComment(comment.id)}
                      >
                        <Heart 
                          size={14} 
                          className="mr-1" 
                          fill={comment.likes && currentUser && comment.likes.includes(currentUser.uid) ? "currentColor" : "none"}
                        />
                        <span>{comment.likesCount || 0}</span>
                      </button>
                      <button 
                        className="ml-4 text-gray-500 hover:text-blue-500"
                        onClick={() => toggleReplyForm(comment.id)}
                      >
                        Reply
                      </button>
                    </div>

                    {/* Reply Form */}
                    {replyingTo === comment.id && (
                      <div className="mt-3 flex space-x-2">
                        <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                          <img 
                            src={userProfile?.photos?.[0] || 'https://via.placeholder.com/24'} 
                            alt="Your avatar" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-full text-sm"
                            placeholder="Write a reply..."
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            disabled={submittingComment}
                          />
                          <button 
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-green-500 disabled:text-gray-300"
                            disabled={!replyText.trim() || submittingComment}
                            onClick={() => handleSubmitReply(comment.id)}
                          >
                            <Send size={16} />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Replies Toggle */}
                    {(comment.replies?.length > 0 || comment.repliesCount > 0) && (
                      <button 
                        className="mt-2 text-green-500 text-xs flex items-center"
                        onClick={() => toggleExpandReplies(comment.id)}
                      >
                        {expandedComments[comment.id] ? (
                          <>
                            <ChevronUp size={14} className="mr-1" />
                            Hide replies ({comment.replies?.length || comment.repliesCount})
                          </>
                        ) : (
                          <>
                            <ChevronDown size={14} className="mr-1" />
                            View replies ({comment.replies?.length || comment.repliesCount})
                          </>
                        )}
                      </button>
                    )}

                    {/* Replies */}
                    {expandedComments[comment.id] && comment.replies?.length > 0 && (
                      <div className="ml-4 mt-2 space-y-3">
                        {comment.replies.map(reply => (
                          <div key={reply.id} className="flex space-x-2">
                            <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                              <img 
                                src={reply.author?.avatar || 'https://via.placeholder.com/24'} 
                                alt={reply.author?.name || 'User'} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="bg-gray-50 rounded-lg p-2">
                                <div className="flex justify-between items-start">
                                  <div className="font-medium text-xs text-gray-900">
                                    {reply.author?.name || 'User'}
                                    {reply.author?.username && (
                                      <span className="font-normal text-gray-500 text-xs ml-1">
                                        @{reply.author.username}
                                      </span>
                                    )}
                                  </div>
                                  {currentUser && reply.authorId === currentUser.uid && (
                                    <button 
                                      className="text-gray-400 hover:text-red-500"
                                      onClick={() => handleDeleteComment(reply.id, true, comment.id)}
                                    >
                                      <Trash size={12} />
                                    </button>
                                  )}
                                </div>
                                <p className="text-gray-800 text-xs mt-1">{reply.content}</p>
                              </div>
                              
                              <div className="mt-1 text-xs flex items-center px-1">
                                <span className="text-gray-500 text-xs">
                                  {reply.createdAt ? formatTimestamp(reply.createdAt) : 'Just now'}
                                </span>
                                <button 
                                  className={`ml-3 flex items-center ${
                                    reply.likes && currentUser && reply.likes.includes(currentUser.uid)
                                      ? 'text-red-500'
                                      : 'text-gray-500 hover:text-red-500'
                                  }`}
                                  onClick={() => handleLikeComment(reply.id)}
                                >
                                  <Heart 
                                    size={12} 
                                    className="mr-1" 
                                    fill={reply.likes && currentUser && reply.likes.includes(currentUser.uid) ? "currentColor" : "none"}
                                  />
                                  <span>{reply.likesCount || 0}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostDetailPage;